import { QuestionFormValues } from '@/utils/forms/questionTransformers';

interface OptionDetailsPanelProps {
  option: QuestionFormValues['options'][number];
  question: QuestionFormValues;
}

export function OptionDetailsPanel({ option, question }: OptionDetailsPanelProps) {
  const hasPlayerData = !!(
    option.grading_data?.playerId ||
    option.grading_data?.playerName ||
    question.question_metadata?.grading?.config?.playerName
  );
  const hasTeamData = !!(
    option.grading_data?.teamId ||
    option.grading_data?.teamName ||
    option.third_image_en
  );
  const hasThresholdData =
    option.grading_data?.threshold !== undefined || option.grading_data?.comparison;
  const hasFixtureData = !!option.grading_data?.fixtureData;
  const hasAnyData = hasPlayerData || hasTeamData || hasThresholdData || hasFixtureData;

  // Get player name and position from option grading_data (preferred) or question metadata
  const playerName =
    option.grading_data?.playerName || question.question_metadata?.grading?.config?.playerName;
  const playerPosition =
    option.grading_data?.playerPosition ||
    question.question_metadata?.grading?.config?.playerPosition;

  return (
    <div className="border-l pl-4 space-y-3 text-sm">
      <h4 className="font-semibold text-xs text-muted-foreground uppercase">Details</h4>

      {/* Player Info Section */}
      {hasPlayerData && (
        <div className="space-y-1">
          <div className="font-medium">Player</div>
          {playerName && <div className="text-muted-foreground">{playerName}</div>}
          {playerPosition && (
            <div className="text-xs text-muted-foreground">Position: {playerPosition}</div>
          )}
          {option.grading_data?.playerId && (
            <div className="text-xs text-muted-foreground">ID: {option.grading_data.playerId}</div>
          )}
        </div>
      )}

      {/* Team Info Section */}
      {hasTeamData && (
        <div className="space-y-1">
          <div className="font-medium">Team</div>

          {/* Team Name - Prioritized (NEW) */}
          {option.grading_data?.teamName && (
            <div className="text-muted-foreground">{option.grading_data.teamName}</div>
          )}

          {/* Team Logo - De-emphasized per user request */}
          {option.third_image_en && (
            <img
              src={option.third_image_en}
              alt="Team logo"
              className="w-8 h-8 object-contain opacity-50"
            />
          )}

          {option.grading_data?.teamId && (
            <div className="text-xs text-muted-foreground">ID: {option.grading_data.teamId}</div>
          )}
        </div>
      )}

      {/* Fixture Info Section - Per-Option Fixture Data (NEW) */}
      {hasFixtureData && option.grading_data?.fixtureData && (
        <div className="space-y-1">
          <div className="font-medium">Fixture</div>
          <div className="text-sm text-muted-foreground">
            {option.grading_data.fixtureData.awayTeamName} @{' '}
            {option.grading_data.fixtureData.homeTeamName}
          </div>
          {option.grading_data.fixtureData.scheduledDate && (
            <div className="text-xs text-muted-foreground">
              {new Date(option.grading_data.fixtureData.scheduledDate).toLocaleString()}
            </div>
          )}
          {option.grading_data.fixtureData.status && (
            <div className="text-xs text-muted-foreground capitalize">
              Status: {option.grading_data.fixtureData.status}
            </div>
          )}
        </div>
      )}

      {/* Threshold Info (for over/under) */}
      {hasThresholdData && option.grading_data && (
        <div className="space-y-1">
          <div className="font-medium">Threshold</div>
          <div className="text-muted-foreground">
            {option.grading_data.comparison}: {option.grading_data.threshold}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasAnyData && (
        <div className="text-muted-foreground text-xs italic">No grading data available</div>
      )}
    </div>
  );
}
